import * as React from "react";

import { cn } from "../lib/cn";

/**
 * Minimal accessible toggle switch — pure Tailwind, no Radix dep.
 *
 * Implements the WAI-ARIA Switch pattern: ``role="switch"``,
 * ``aria-checked`` / ``aria-disabled``, native ``<button>`` keyboard
 * activation (Space/Enter), focus ring matching the rest of the primitives.
 *
 * WCAG 1.4.11 (non-text contrast, 3:1): state is conveyed by THREE cues —
 * (1) track background (orange-soft vs cream), (2) thumb position (right
 * vs left), and (3) thumb fill (filled orange dot in ``on``, hollow ring
 * in ``off``) — so a colour-blind / monochrome user still distinguishes
 * the states.
 */
export interface SwitchProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "onClick" | "onChange"
  > {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onCheckedChange, disabled, className, ...props }, ref) => {
    const handleClick = React.useCallback(() => {
      if (disabled) return;
      onCheckedChange(!checked);
    }, [checked, disabled, onCheckedChange]);

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full",
          "border transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Track: orange tint on, deep cream on off. The border darkens
          // on off so the track edge is visible against a card background
          // even at low contrast / monochrome.
          checked
            ? "border-ando-orange/40 bg-ando-orange-soft"
            : "border-ink-3/40 bg-cream-deep hover:bg-cream-deep/80",
          className,
        )}
        {...props}
      >
        <span
          aria-hidden
          className={cn(
            "block h-3.5 w-3.5 rounded-full shadow-sm transition-transform",
            // Thumb position: right when on, left when off.
            checked ? "translate-x-[18px]" : "translate-x-0.5",
            // Non-chromatic state cue — thumb fill differs by state.
            // ``on``  = solid orange dot (visible against orange track).
            // ``off`` = hollow ring (visible against cream track even in
            // monochrome / colour-blind rendering).
            checked
              ? "bg-ando-orange"
              : "border border-ink-3/50 bg-white",
          )}
        />
      </button>
    );
  },
);
Switch.displayName = "Switch";

export { Switch };
